import React, { useState, useEffect } from "react"
import { Offcanvas, CloseButton, Button, Form, Nav } from 'react-bootstrap'
import { FaSortAlphaDown, FaCalendarAlt, FaSyncAlt, FaFileAlt, FaUserTag } from 'react-icons/fa'; 
import XDocList from "./XDocList";
import RoleManager from "./RoleManager"; 
import { getFirestore, collection, getDocs, query, where } from 'firebase/firestore'
import { getAuth } from 'firebase/auth'; 
import { spinner } from "../utils/Helper"
import { toast } from 'react-toastify'
import { CreateDoc } from "./XDocList"; 

import doc1 from '../images/doc.png';
import './xDocList.css';

export default function Docz(props) {
    const { theme = 'dark' } = props;
    const isDark = theme === 'dark';

    const db = getFirestore();
    
    // HYBRID STATE: Fall back to local state if parent isn't managing visibility
    const [localShow, setLocalShow] = useState(false);
    const isCanvasOpen = props.isOpen !== undefined ? props.isOpen : localShow;

    const [xDocz, setXDocz] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: 'created', direction: 'desc' });
    const [activeTab, setActiveTab] = useState("documents"); 
    
    const [userAuthorizedRoleIds, setUserAuthorizedRoleIds] = useState([]);
    const [userEditableRoleIds, setUserEditableRoleIds] = useState([]);
    const [userDeletableRoleIds, setUserDeletableRoleIds] = useState([]);
    const [userCreatableRoles, setUserCreatableRoles] = useState([]); 

    const isPublicView = props.public === true;

    const themeStyles = {
        backdrop: isDark ? "bg-dark adminBackdrop" : "bg-light docLight border-start ",
        offcanvas: isDark ? "bg-dark" : " xlight border-start blur",
        text: isDark ? "text-white" : "text-dark",
        subText: isDark ? "text-white-50" : "text-muted",
        search: isDark ? "glass-search mb-2" : "form-control border-light-subtle  mb-2",
        headerBorder: isDark ? "border-secondary border-opacity-20" : "border-light-subtle",
        title: isDark ? "modified xTitle animated rollIn m-0" : "  xTitle text-dark animated rollIn"
    };

    const handleToggleClose = () => {
        if (props.onToggle) props.onToggle();
        else setLocalShow(false);
    };

    const handleToggleOpen = () => {
        if (props.onToggle) props.onToggle();
        else setLocalShow(true);
    };

    useEffect(() => {
        const currentUser = getAuth().currentUser;
        if (!currentUser || !isCanvasOpen) return;

        const fetchUserPermissions = async () => {
            try {
                let absoluteProfileId = currentUser.uid;

                if (currentUser.email) {
                    const profileQuery = query(
                        collection(db, "profiles"), 
                        where("email", "==", currentUser.email)
                    );
                    const profileSnapshot = await getDocs(profileQuery);
                    if (!profileSnapshot.empty) {
                        absoluteProfileId = profileSnapshot.docs[0].id;
                    }
                }

                const rolesSnapshot = await getDocs(collection(db, "roles"));
                const clearReaderRoles = [];
                const clearEditorRoles = [];
                const clearDeleterRoles = [];
                const clearCreatorRoles = [];

                rolesSnapshot.docs.forEach(docSnap => {
                    const data = docSnap.data();
                    
                    const matchPattern = (arr) => arr && (
                        arr.includes(absoluteProfileId) ||
                        arr.includes(currentUser.uid) ||
                        (currentUser.email && arr.includes(currentUser.email))
                    );

                    if (matchPattern(data.allowedCreators)) {
                        clearCreatorRoles.push({ id: docSnap.id, roleName: data.roleName });
                    }
                    if (matchPattern(data.allowedEditors)) clearEditorRoles.push(docSnap.id);
                    if (matchPattern(data.allowedDeleters)) clearDeleterRoles.push(docSnap.id);
                    if (matchPattern(data.allowedEditors) || matchPattern(data.allowedReaders)) clearReaderRoles.push(docSnap.id);
                });

                setUserEditableRoleIds(clearEditorRoles);
                setUserDeletableRoleIds(clearDeleterRoles);
                setUserAuthorizedRoleIds(clearReaderRoles);
                setUserCreatableRoles(clearCreatorRoles);
            } catch (err) {
                console.error("RBAC Resolution Error:", err);
            }
        };

        fetchUserPermissions();
    }, [isCanvasOpen, db]);

    // RESTORED: Fetching data strictly loads data and does not force side-effect toggling
    const getXDocs = () => {
        spinner(true);
        const docsRef = collection(db, "docs");
        
        getDocs(query(docsRef)).then((snapshot) => {
            let rec = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
            setXDocz(rec);
            spinner(false);
        }).catch(err => {
            spinner(false);
            console.error("Firestore Error:", err);
            toast.error(err.message);
        });
    }

    const handleInitialClick = () => {
        getXDocs();
        handleToggleOpen();
    };

    const getLastUpdatedDate = () => {
        if (xDocz.length === 0) return "N/A";
        const latest = xDocz.reduce((max, doc) => {
            const current = doc.updated?.seconds || doc.created?.seconds || 0;
            return current > max ? current : max;
        }, 0);
        return latest === 0 ? "N/A" : new Date(latest * 1000).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric'
        });
    };

    const toggleSort = (key) => {
        let direction = 'desc';
        if (sortConfig.key === key && sortConfig.direction === 'desc') {
            direction = 'asc';
        }
        setSortConfig({ key, direction });
    };

    const filteredAndSortedDocs = xDocz
        .filter(doc => {
            const matchesSearch = doc.name?.toLowerCase().includes(searchTerm.toLowerCase());
            if (!matchesSearch) return false;
            if (!isPublicView) return true;

            if (doc.isPrivate === true) return false;
            const documentRoles = doc.associatedRoles || [];
            if (documentRoles.length > 0) {
                return documentRoles.some(roleId => userAuthorizedRoleIds.includes(roleId));
            }
            return true;
        })
        .sort((a, b) => {
            let valA = a[sortConfig.key];
            let valB = b[sortConfig.key];
            if (sortConfig.key === 'created') {
                valA = a.created?.seconds || 0;
                valB = b.created?.seconds || 0;
            } else if (typeof valA === 'string') {
                valA = valA.toLowerCase();
                valB = valB.toLowerCase();
            }
            if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
            if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

    return (
        <>
            {isPublicView ? (
              
                <Button onClick={handleInitialClick} className="">
                    <img src={doc1} alt="Document Icon" className="thumb hoverX animated fadeInRight" />
                    <br />
                    <span>  Documents</span>
                </Button>
            ) : (
                
                <Button variant={isDark ? "primary" : "outline-primary"} onClick={handleInitialClick} className="hoverX shadow-sm">
                    <span className="circle-btn centerCircle">🎲</span>Docz
                </Button>
            )}

            <Offcanvas
                show={isCanvasOpen}
                onHide={handleToggleClose}
                placement="end"
                className={themeStyles.offcanvas}
                backdropClassName={themeStyles.backdrop}
                style={{ 
                    width: '100%', 
                    maxWidth: !isPublicView ? '550px' : '550px' 
                }}
            >
                <Offcanvas.Header className={`pb-2 border-bottom ${themeStyles.headerBorder}`}>
                    <div className="w-100">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h3 className={themeStyles.title}>{isPublicView ? "Docz" : "Docz"}</h3>
                            <div className="d-flex align-items-center gap-2">
                                {!isPublicView && <CreateDoc theme={theme} onSuccess={getXDocs} />}
                                <CloseButton variant={isDark ? "white" : undefined} onClick={handleToggleClose} />
                            </div>
                        </div>

                        {/* FIXED RESPONSIVE BREAKPOINT FLOW STYLE FROM ADMIN */}
                        {!isPublicView && (
                            <Nav
                                variant="pills"
                                activeKey={activeTab}
                                onSelect={(k) => setActiveTab(k)}
                                className="flex-column flex-sm-row gap-2 mb-3 profile-nav-dark"
                            >
                                <Nav.Item className="flex-fill">
                                    <Nav.Link eventKey="documents" className="text-center py-2 d-flex align-items-center justify-content-center gap-2">
                                        <FaFileAlt size={14}  /> Files Directory
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item className="flex-fill">
                                    <Nav.Link eventKey="roles" className="text-center py-2 d-flex align-items-center justify-content-center gap-2">
                                        <FaUserTag size={14}  /> Role Manager
                                    </Nav.Link>
                                </Nav.Item>
                            </Nav>
                        )}

                        {isPublicView && userCreatableRoles.length > 0 && activeTab === "documents" && (
                            <div className="d-flex flex-wrap gap-2 mb-3 align-items-center justify-content-start border-bottom pb-2 border-secondary border-opacity-10">
                                {userCreatableRoles.map((roleObj) => (
                                    <CreateDoc 
                                        key={roleObj.id} 
                                        theme={theme} 
                                        onSuccess={getXDocs} 
                                        preSelectedRoleId={roleObj.id} 
                                        buttonLabel={`+ New ${roleObj.roleName}`}
                                    />
                                ))}
                            </div>
                        )}

                        {(isPublicView || activeTab === "documents") && (
                            <>
                                <Form.Control type="search" placeholder="🔎 Search docs..." className={themeStyles.search} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                                <div className="d-flex align-items-center justify-content-between mb-3 ps-1">
                                    <div className="d-flex align-items-center gap-2" style={{ opacity: 0.7 }}>
                                        <FaSyncAlt className="text-info pointer hover-spin" size={12} onClick={getXDocs} />
                                        <span className={`meta-label m-0 ${themeStyles.subText}`} style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                                            LAST UPDATED: <span className={themeStyles.text}>{getLastUpdatedDate()}</span>
                                        </span>
                                    </div>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div className="d-flex gap-2">
                                        <button type="button" className={`btn-sort-pill ${sortConfig.key === 'created' ? 'active' : ''}`} onClick={() => toggleSort('created')}><FaCalendarAlt className="me-1" size={10} />Date {sortConfig.key === 'created' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</button>
                                        <button type="button" className={`btn-sort-pill ${sortConfig.key === 'name' ? 'active' : ''}`} onClick={() => toggleSort('name')}><FaSortAlphaDown className="me-1" size={10} />Name {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</button>
                                    </div>

                               
                                    <div className="badge rounded-pill bg-danger" style={{ fontSize: '.7rem', letterSpacing: '0.5px' }} ><span className="value">{filteredAndSortedDocs.length} Docs</span></div>
                                </div>
                            </>
                        )}
                    </div>
                </Offcanvas.Header>

                <Offcanvas.Body className="custom-scrollbar">
                    {isPublicView || activeTab === "documents" ? (
                        <ul className="doc-container p-0 padding">
                            {filteredAndSortedDocs.map((x) => {
                                const documentRoles = x.associatedRoles || [];
                                const dynamicHasEditAccess = documentRoles.some(roleId => userEditableRoleIds.includes(roleId));
                                const dynamicHasDeleteAccess = documentRoles.some(roleId => userDeletableRoleIds.includes(roleId));
                                
                                return (
                                    <XDocList
                                        key={x.id}
                                        {...x}
                                        theme={theme}
                                        public={isPublicView}
                                        onDeleteSuccess={getXDocs}
                                        isAdmin={!isPublicView || dynamicHasEditAccess} 
                                        canDeleteAttachments={!isPublicView || dynamicHasDeleteAccess} 
                                    />
                                );
                            })}
                        </ul>
                    ) : (
                        <RoleManager theme={theme} documentsList={xDocz} refreshDocs={getXDocs} />
                    )}
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
}